import Button, { ButtonProps as V2ButtonProps } from '@components/v2/ui/Button/Button';
import Icon from '@components/v2/ui/Icon/Icon';
import { CircularProgress, SxProps, Theme } from '@mui/material';
import { ButtonProps } from '@mui/material/Button';
import { Check, Info } from 'lucide-react';
import { always } from 'ramda';
import { ReactNode, useEffect } from 'react';
import { makeStyles } from 'tss-react/mui';
import LangEnum from '../lang/LangEnum';
import { useTranslation } from '../modules/translation/TranslationContext';

type UseStylesProps = {
  iconVariant?: 'inherit';
};

const useStyles = makeStyles<UseStylesProps>()((theme, { iconVariant }) => ({
  button: {
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
  },
  startAdornment: {
    color: iconVariant === 'inherit' ? theme.palette.text.primary : undefined,
    marginRight: theme.spacing(1),
  },
  loadingIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  successButton: {
    backgroundColor: theme.palette.success.main,
    '&:hover': {
      backgroundColor: theme.palette.success.main,
    },
    '&:active': {
      boxShadow: 'none',
      backgroundColor: theme.palette.success.main,
    },
    color: theme.palette.common.white,
  },
  errorButton: {
    backgroundColor: theme.palette.error.main,
    '&:hover': {
      backgroundColor: theme.palette.error.main,
    },
    '&:active': {
      boxShadow: 'none',
      backgroundColor: theme.palette.error.main,
    },
    color: theme.palette.common.white,
  },
}));

export type ButtonStatusVariant = 'normal' | 'loading' | 'success' | 'error';

type ButtonWithFeddbackProps = {
  loadingLabel?: string;
  successLabel?: string;
  errorLabel?: string;
  status?: ButtonStatusVariant;
  onStatusShowed?: (lastStatus: ButtonStatusVariant) => void;
  iconVariant?: UseStylesProps['iconVariant'];
  testId?: string;
} & ButtonProps;

type StatusConfig = {
  className: string;
  label: ReactNode;
  startIcon?: ReactNode;
  disabled?: boolean;
};

const ButtonWithFeddback = (props: ButtonWithFeddbackProps) => {
  const { classes, cx } = useStyles({ iconVariant: props.iconVariant });
  const { translate } = useTranslation();
  const {
    children,
    loadingLabel = translate(LangEnum.LOGIN_BUTTONFEEDBACK_WAIT),
    successLabel = translate(LangEnum.GLOBAL_SUCCESS),
    errorLabel = 'Erro!',
    status = 'normal',
    onStatusShowed = always,
    iconVariant,
    className,
    style,
    sx,
    testId,
    ...rest
  } = props;

  useEffect(() => {
    if (status === 'normal') {
      return;
    }

    const handleStatusChanged = () => {
      onStatusShowed(status);
    };

    const statusChangeTimeout = setTimeout(handleStatusChanged, 2000);
    return () => clearTimeout(statusChangeTimeout);
  }, [status, onStatusShowed]);

  const statusConfig: Record<ButtonStatusVariant, StatusConfig> = {
    normal: {
      className: cx(classes.button, className),
      label: children,
    },
    loading: {
      className: cx(classes.button, className),
      label: loadingLabel,
      startIcon: <CircularProgress size={16} className={classes.loadingIcon} />,
      disabled: true,
    },
    success: {
      className: cx(classes.successButton, classes.button, className),
      label: successLabel || children,
      startIcon: <Icon icon={Check} size="lg" className={classes.startAdornment} />,
    },
    error: {
      className: cx(classes.errorButton, classes.button, className),
      label: errorLabel || children,
      startIcon: <Icon icon={Info} size="lg" className={classes.startAdornment} />,
    },
  };

  const config = statusConfig[status];

  const sxEntries: SxProps<Theme>[] = [];
  if (style) {
    sxEntries.push(style as SxProps<Theme>);
  }
  if (sx) {
    sxEntries.push(sx);
  }
  const mergedSx = sxEntries.length > 0 ? (sxEntries as SxProps<Theme>) : undefined;

  return (
    <Button
      {...(rest as unknown as V2ButtonProps)}
      testId={testId}
      className={config.className}
      disabled={config.disabled || rest.disabled}
      startIcon={config.startIcon ?? rest.startIcon}
      sx={mergedSx}
    >
      {config.label}
    </Button>
  );
};

export default ButtonWithFeddback;
